import React, { useEffect, useMemo, useState } from "react"
import { ReactifySearchProvider, Sensors } from "@usereactify/search"
import { useAppContext } from "@providers/app"
import { useConfigContext } from "@providers/config"
import GiftRegistryDrawer from "@app/components/GiftRegistry/Drawer"

import { useCore } from "@app/hooks/useCore"
import { useContent } from "@app/hooks/useContent"
import { useShopify } from "@app/hooks/useShopify"
import useCollectionPageSettings from "@app/hooks/useCollectionPageSettings"

import CollectionBanner from "./CollectionBanner"
import ProductGrid from "@components/Sections/ProductGrid"
import PageSummary from "@components/PageSummary"
import GiftRegistryPasswordDrawer from "../GiftRegistry/PasswordDrawer"

import type { PageProps } from "@root/types/global"
import type { Props } from "@app/templates/collection"

import { Box, Container, Flex, Spinner } from "@chakra-ui/react"
import { useYotpoScript } from "@app/hooks/useYotpoScript"

const Collection: React.FC<PageProps<Props>> = ({ collection, location }) => {
  const {
    helpers: { isBrowser },
  } = useCore()
  const { activeCollection, setActiveCollection } = useAppContext()
  const {
    store: { shopifyShopDomain },
    settings: { search },
  } = useConfigContext()

  const { productGridSection } = useCollectionPageSettings()
  const { getContent } = useContent()
  const { collectionNormaliser } = useShopify()

  // Quantity used to pass quantity to the GR drawer from the quick view add to cart drawer
  const [quantity, setQuantity] = React.useState(1)
  const [grProductData, setGrProductData] = React.useState()
  const [showPasswordDrawer, setShowPasswordDrawer] = useState(false)
  const [giftRegistryId, setGiftRegistryId] = useState()

  const setDrawerOff = () => {
    setShowPasswordDrawer(false)
  }

  const item = useMemo(() => collectionNormaliser(collection), [collection, collectionNormaliser])
  const textSeo = useMemo(() => getContent({ content: item?.bottomText }), [getContent, item?.bottomText])
  useEffect(() => {
    if (item?.id !== activeCollection?.id) {
      setActiveCollection(item)
    }
  }, [item, activeCollection?.id, setActiveCollection])
  const yotpoIdentifier = isBrowser ? collection?.id + window.location?.search : collection?.id
  useYotpoScript(yotpoIdentifier)
  return (
    <Box>
      <Box minHeight={`calc(100vh - 135px)`}>
        <CollectionBanner collection={item} location={location} description={textSeo} />
        <ReactifySearchProvider
          mode="collection"
          collectionHandle={item.handle}
          shopifyPermanentDomain={shopifyShopDomain}
          renderBooting={() => (
            <Flex alignItems={`center`} justifyContent={`center`} py={6}>
              <Spinner />
            </Flex>
          )}
          includeFields={search.searchIncludeFields}
          excludeFields={search.searchExcludeFields}
        >
          <Sensors />
          <Container>
            <ProductGrid {...productGridSection} collection={collection} setQuantity={setQuantity} setGrProductData={setGrProductData} />
          </Container>
        </ReactifySearchProvider>
      </Box>
      {item.descriptionHtml && (
        <PageSummary>
          <div dangerouslySetInnerHTML={{ __html: item.descriptionHtml }} />
        </PageSummary>
      )}
      <GiftRegistryDrawer
        quantity={quantity}
        grProductData={grProductData}
        setShowPasswordDrawer={setShowPasswordDrawer}
        setGiftRegistryId={setGiftRegistryId}
      />
      {showPasswordDrawer && (
        <GiftRegistryPasswordDrawer registry={{ Id: giftRegistryId, isPasswordProtected: true }} setDrawerOff={setDrawerOff} />
      )}
    </Box>
  )
}

export default React.memo(Collection)
